import { phanesAPIClient } from '../api/clients';
import { ComponentType } from '../enums/ProvisioningFieldTypes';
import { ProvisioningStatus } from '../enums/ProvisioningStatus';
import {
  IEntitlementInstances,
  IProductInstance,
  IProvisioningMetaData,
  IProvisioningQuestion,
} from '../interfaces/ICustomersLists';
import { IGenericTagProps } from '../interfaces/IProductGenericTag';
import {
  IProductTransformed,
  IProductDTO,
  IProductListErrorResponse,
  IProductListSuccessResponse,
  IProductTransformedData,
  IProductObjectTransformed,
  IConvertedProvisioningMetadata,
} from '../interfaces/IProductList';
import {
  Activated,
  External,
  InActive,
  Legacy,
  Provisioned,
  Trial,
} from '../utils/constant';

function getProvisioningQuestionsMetaData(
  provisioningQuestions: IProvisioningQuestion[],
  provisioningMetadata: IProvisioningMetaData[]
): IConvertedProvisioningMetadata[] {
  if (provisioningQuestions.length > 0) {
    return provisioningQuestions.map((provisioningQuestion) => {
      let questionValue = '';
      if (
        provisioningQuestion.type === ComponentType.DROP_DOWN &&
        provisioningQuestion.options.length === 1
      ) {
        questionValue = provisioningQuestion.options[0].value;
      }
      return {
        name: provisioningQuestion.name,
        value: questionValue,
      };
    });
  } else {
    return provisioningMetadata;
  }
}

function getProductObject(
  product: IProductInstance
): IProductObjectTransformed {
  return {
    productId: product?.ID,
    activationCode: product?.activationCode,
    instanceId: product?.external_tenant_id,
    name: product?.product.name,
    provisionStatus: product?.provisioning_status,
    region: product?.region,
    isTrial: product?.is_trial ?? false,
    isActive: product?.isActive ?? true,
    isHidden: product?.isHidden ?? false,
    isLegacy: product?.isLegacy ?? false,
    isEA: product?.isEA ?? false,
    productAllowedRegions: product?.product?.product_allowed_regions,
    initialAdmin: product?.initialAdmin ?? '',
    productTags: getProductFlags(product),
    provisioningQuestionsMetaData: getProvisioningQuestionsMetaData(
      product?.product?.provisioning_questions ?? [],
      product?.product?.provisioning_metadata ?? []
    ),
    provisioningQuestions: product?.product?.provisioning_questions ?? [],
    isOrgEntitledProductCard: Object.prototype.hasOwnProperty.call(
      product,
      'org_entitlement_instances'
    ),
  };
}

function getProductName(
  product: IProductObjectTransformed,
  iEntitlementInstance: IEntitlementInstances,
  subUiFeatureFlagExist: boolean = false,
  unifiedOnboardingFeatureFlagExist: boolean = false
): string {
  if (subUiFeatureFlagExist && iEntitlementInstance?.entitlement?.description) {
    if (
      (unifiedOnboardingFeatureFlagExist &&
        product?.provisionStatus === 'FINISHED') ||
      (!unifiedOnboardingFeatureFlagExist &&
        product?.provisionStatus !== 'NEW' &&
        product?.provisionStatus !== 'ACTION_REQUIRED')
    ) {
      return Object.prototype.hasOwnProperty.call(
        product,
        'org_entitlement_instances'
      )
        ? iEntitlementInstance?.entitlement?.description
        : `${product.name} ${iEntitlementInstance?.entitlement?.description}`;
    }
  }
  return product?.name;
}

export const getProductFlags = (
  product: IProductInstance
): IGenericTagProps[] => {
  const productTags: IGenericTagProps[] = [];
  const conditionsAndTags = [
    { condition: product.is_trial, tag: Trial },
    { condition: product.isLegacy, tag: Legacy },
    { condition: !product.isActive, tag: InActive },
    {
      condition:
        product.isActive &&
        ![ProvisioningStatus.IN_PROGRESS, ProvisioningStatus.ERROR].includes(
          product.provisioning_status as ProvisioningStatus
        ),
      tag: Activated,
    },
    {
      condition: [
        ProvisioningStatus.IN_PROGRESS,
        ProvisioningStatus.ERROR,
      ].includes(product.provisioning_status as ProvisioningStatus),
      tag: Provisioned,
    },
    { condition: product.isExternal, tag: External },
  ];
  conditionsAndTags.forEach(({ condition, tag }) => {
    if (condition) {
      productTags.push(tag);
    }
  });
  return productTags;
};

function getEntitlements(product: IProductInstance): IEntitlementInstances[] {
  let productEntitlements =
    product?.org_entitlement_instances ?? product.entitlement_instances;
  if (product.provisioning_status !== ProvisioningStatus.FINISHED) {
    productEntitlements = productEntitlements.filter((productEntitlement) => {
      return !productEntitlement?.Deleted;
    });
  } else {
    productEntitlements = productEntitlements.filter((productEntitlement) => {
      return (
        productEntitlement?.entitlement?.description !== '' ||
        !productEntitlement?.entitlement?.is_service
      );
    });
  }
  return productEntitlements;
}

function getDifferentTilesData(
  productEntitlements: IEntitlementInstances[],
  productObject: IProductObjectTransformed,
  subUiFeatureFlagExist: boolean = false,
  unifiedOnboardingFeatureFlagExist: boolean = false
): IProductTransformed[] {
  const finalProductObject: IProductTransformed[] = [];
  for (const productEntitlement of productEntitlements) {
    finalProductObject.push({
      ...productObject,
      ...getSubscriptionData(productEntitlement, productObject),
      name: getProductName(
        productObject,
        productEntitlements[0],
        subUiFeatureFlagExist,
        unifiedOnboardingFeatureFlagExist
      ),
    });
  }
  return finalProductObject;
}

function getSubscriptionData(
  productEntitlement: IEntitlementInstances,
  productObject: IProductObjectTransformed,
  subUiFeatureFlagExist: boolean = false,
  unifiedOnboardingFeatureFlagExist: boolean = false
): IProductTransformed {
  const subscriptionData = productObject.isTrial
    ? productEntitlement?.trial
    : productEntitlement?.subscription;
  return {
    ...productObject,
    name: getProductName(
      productObject,
      productEntitlement,
      subUiFeatureFlagExist,
      unifiedOnboardingFeatureFlagExist
    ),
    isService: false,
    seat: productEntitlement?.quantity ?? '-',
    tier: productEntitlement?.entitlement?.value ?? '',
    productDescription: productEntitlement?.entitlement?.description || '',
    subscriptionId: subscriptionData?.externalId || '-',
    startDate:
      (productObject.isTrial
        ? subscriptionData.trial_start_date
        : subscriptionData.requested_start_date) ?? '',
    expiryDate:
      (productObject.isTrial
        ? subscriptionData.trial_end_date
        : subscriptionData.subscription_end_date) ?? '',
    isExternal: subscriptionData.is_external ?? false,
    productName: subscriptionData.product_name ?? '',
    canProvision: subscriptionData.is_provisionable ?? false,
  };
}

function getFinalProductObject(
  unifiedOnboardingFeatureFlagExist: boolean,
  product: IProductInstance,
  productEntitlements: IEntitlementInstances[],
  productObj: IProductObjectTransformed,
  subUiFeatureFlagExist: boolean = false
): IProductTransformed[] {
  let finalProductObject = [];
  if (
    unifiedOnboardingFeatureFlagExist &&
    Object.prototype.hasOwnProperty.call(
      product,
      'org_entitlement_instances'
    ) &&
    productEntitlements.length > 1 &&
    product?.provisioning_status === 'FINISHED'
  ) {
    finalProductObject = [
      ...getDifferentTilesData(
        productEntitlements,
        productObj,
        subUiFeatureFlagExist,
        unifiedOnboardingFeatureFlagExist
      ),
    ];
  } else {
    finalProductObject.push({
      ...productObj,
      ...getSubscriptionData(
        productEntitlements[0],
        productObj,
        subUiFeatureFlagExist,
        unifiedOnboardingFeatureFlagExist
      ),
    });
  }
  return finalProductObject;
}

function getKeyToPush(
  unifiedOnboardingFeatureFlagExist: boolean,
  productObject: IProductObjectTransformed
): keyof IProductTransformedData {
  if (unifiedOnboardingFeatureFlagExist) {
    return productObject.provisionStatus === 'FINISHED'
      ? 'otherProducts'
      : 'pendingProducts';
  } else {
    return productObject.provisionStatus === 'NEW' ||
      productObject.provisionStatus === 'ACTION_REQUIRED'
      ? 'pendingProducts'
      : 'otherProducts';
  }
}
export const fetchProductList = async (
  enterpriseId: string,
  unifiedOnboardingFeatureFlagExist: boolean = false,
  subUiFeatureFlagExist: boolean = false
): Promise<IProductListSuccessResponse | IProductListErrorResponse> => {
  try {
    const response: IProductDTO = await phanesAPIClient.get(
      `/v1/customers?enterprise_id=${enterpriseId}`
    );
    let productTransformedData: IProductTransformedData = {
      allProducts: [],
      pendingProducts: [],
      otherProducts: [],
    };

    if (response.data) {
      productTransformedData = response.data.reduce(
        (finalData: IProductTransformedData, product) => {
          const productObject: IProductObjectTransformed =
            getProductObject(product);
          const productEntitlements = getEntitlements(product);
          if (productEntitlements.length === 0) {
            return finalData;
          }
          const finalProductObject = getFinalProductObject(
            unifiedOnboardingFeatureFlagExist,
            product,
            productEntitlements,
            productObject,
            subUiFeatureFlagExist
          );

          finalData[
            getKeyToPush(unifiedOnboardingFeatureFlagExist, productObject)
          ].push(...finalProductObject);
          finalData.allProducts.push(...finalProductObject);
          return finalData;
        },
        {
          allProducts: [],
          pendingProducts: [],
          otherProducts: [],
        }
      );
    }

    return {
      isOk: true,
      data: productTransformedData,
      error: null,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
    return {
      isOk: false,
      data: null,
      error,
    };
  }
};
